/**
 * lib/sheet.js -- Comportamiento de la "hoja inferior" de los dialogos en movil.
 * El diseno vive en css/dialog-sheet.css; aqui solo estan dos cosas:
 *
 * - isSheet(overlay): ¿este dialogo se esta presentando como hoja AHORA? Lo decide el
 *   CSS (publica --dialog-presentation: sheet dentro de su media query). Asi JS no
 *   repite el breakpoint ni la lista de opt-outs, y no se pueden desincronizar.
 * - enableSheetDrag(overlay, getClose): arrastrar el asa o el encabezado hacia abajo
 *   cierra el dialogo. Comparte el cierre del boton X (`getClose()`), por lo que
 *   conserva todo lo que ese cierre ya hace (animacion de salida, foco, estado).
 *
 * El cuerpo que hace scroll NUNCA inicia el arrastre: ese gesto es del scroll.
 */
import '../css/dialog-sheet.css';

// Umbrales del gesto: cierra al pasar de 100px, o con un arrastre corto pero rapido.
const CLOSE_DISTANCE = 100; // px
const FLICK_DISTANCE = 30; // px minimos para considerar un "flick"
const FLICK_SPEED = 0.5; // px/ms
const SPRING_BACK = { duration: 200, easing: 'cubic-bezier(.16, 1, .3, 1)' };

export function isSheet(overlay) {
  return getComputedStyle(overlay).getPropertyValue('--dialog-presentation').trim() === 'sheet';
}

/**
 * @param {HTMLElement} overlay  .modal-overlay (o .navigation-more-overlay)
 * @param {() => (undefined | (() => void))} getClose  devuelve el cierre vigente, o
 *   undefined si el dialogo no se puede cerrar ahora (p. ej. guardando).
 * @returns {() => void} cleanup
 */
export function enableSheetDrag(overlay, getClose) {
  let drag = null;
  const box = () => overlay.querySelector('.modal-box, .navigation-more');

  function onPointerDown(event) {
    const grip = event.target.closest('.sheet-handle')
      || (event.target.closest('.modal-header') && !event.target.closest('button, a, input, select, textarea'));
    if (!grip || !getClose() || !isSheet(overlay) || overlay.dataset.motionClosing) return;
    drag = { y: event.clientY, time: performance.now(), dy: 0 };
    box().setPointerCapture(event.pointerId);
  }

  function onPointerMove(event) {
    if (!drag) return;
    drag.dy = Math.max(0, event.clientY - drag.y);
    box().style.transform = `translateY(${drag.dy}px)`;
  }

  function onRelease() {
    if (!drag) return;
    const { dy, time } = drag;
    drag = null;
    const speed = dy / Math.max(1, performance.now() - time);
    const close = getClose();
    if (close && (dy > CLOSE_DISTANCE || (dy > FLICK_DISTANCE && speed > FLICK_SPEED))) {
      close(); // la salida continua desde donde la hoja quedo (ver dialogMotion.js)
      return;
    }
    box().animate([{ transform: `translateY(${dy}px)` }, { transform: 'none' }], SPRING_BACK);
    box().style.transform = '';
  }

  overlay.addEventListener('pointerdown', onPointerDown);
  overlay.addEventListener('pointermove', onPointerMove);
  overlay.addEventListener('pointerup', onRelease);
  overlay.addEventListener('pointercancel', onRelease);
  return () => {
    overlay.removeEventListener('pointerdown', onPointerDown);
    overlay.removeEventListener('pointermove', onPointerMove);
    overlay.removeEventListener('pointerup', onRelease);
    overlay.removeEventListener('pointercancel', onRelease);
  };
}
